import { useQuery } from '@apollo/client';
// todo: remove lodash on client
import startCase from 'lodash/startCase';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import goldenRatio from '@glass/common/modules/goldenRatio/goldenRatio';
import { USD_CURRENCY } from '@glass/common/modules/payments/currencies/types';
import LinearProgress from '@glass/web/components/base/LinearProgress';
import Typography from '@glass/web/components/base/Typography';
import InlineCenterSkeleton from '@glass/web/components/Skeleton/InlineCenterSkeleton';
import { SALARY_SECTION_ID } from '@glass/web/modules/content/pages/contentIds';
import { FLEX } from '@glass/web/modules/theme/flexConstants';
import makeStyles from '@glass/web/modules/theme/makeStyles';
import { DEFAULT_SPACING } from '@glass/web/modules/theme/spacing';

import JsonLDScript from '@glass/shared/components/Head/JsonLDScript';
import useOccupationSalaryJsonLd from '@glass/shared/components/Section/SalarySection/useOccupationSalaryJsonLd';
import Section from '@glass/shared/components/Section/Section';
import usePageTerms from '@glass/shared/modules/content/staticVariations/usePageTerms';
import salaryQuery from '@glass/shared/modules/resources/queries/salary.client.graphql';
import withTrackingContext from '@glass/shared/modules/tracking/withTrackingContext';
import selectUserStateCode from '@glass/shared/modules/userConfig/selectors/selectUserStateCode';

const useStyles = makeStyles()((theme) => ({
  priceContainer: {
    display: FLEX,
    marginBottom: theme.spacing(DEFAULT_SPACING / goldenRatio),
  },
  price: {
    display: FLEX,
    alignItems: 'baseline',
  },
  rightPrice: {
    marginLeft: 'auto',
  },
  progressContainer: {
    padding: theme.spacing(1, 0),
  },
}));

const SalarySection = React.forwardRef(
  ({ title, subtitle, search: searchProp, isAlternatingColor, type: typeProp }, ref) => {
    const geoState = useSelector(selectUserStateCode);
    const { locale } = useRouter();
    const { pageTerm } = usePageTerms();
    const search = searchProp || pageTerm;

    // todo: add currency conversion
    const { data, loading } = useQuery(salaryQuery, {
      variables: { search, state: geoState },
      skip: !search,
      ssr: false,
    });

    const { classes, cx } = useStyles();

    const fallback = __DEV__ && search ? { min: 1, max: 10, average: 5 } : {};

    const { average, min, max, city, state, type } = data?.salary?.average ? data.salary : fallback;

    const location = useMemo(() => {
      if (type === 'area' && state) {
        const capState = state.toUpperCase();
        if (city) {
          return `${startCase(city)}, ${capState}, US`;
        }
        return `${capState}, US`;
      }
      return 'the US';
    }, [type, city, state]);

    const occupationSalaryJsonLd = useOccupationSalaryJsonLd({
      name: search,
      location,
      salary: data?.salary,
    });

    const ratio = useMemo(
      () => (!loading ? Math.round((100 * (average - min)) / (max - min)) : 50),
      [loading, average, min, max],
    );

    const numberFormat = useMemo(
      () => new Intl.NumberFormat(locale, { style: 'currency', currency: USD_CURRENCY }),
      [locale],
    );

    const minText = useMemo(() => numberFormat.format(min), [min, numberFormat]);
    const avgText = useMemo(() => numberFormat.format(average), [average, numberFormat]);
    const maxText = useMemo(() => numberFormat.format(max), [max, numberFormat]);

    if (!loading && (!min || !max)) {
      return null;
    }

    const loadingText = loading ? <InlineCenterSkeleton inline width={60} /> : null;

    return (
      <Section
        ref={ref}
        isAlternatingColor={isAlternatingColor}
        subtitle={subtitle}
        title={title}
        type={typeProp}
      >
        {!loading ? <JsonLDScript schema={occupationSalaryJsonLd} /> : null}
        <div>
          <Typography variant="h6">
            {search} salary in {location}
          </Typography>
          <div className={classes.progressContainer}>
            <LinearProgress color="primary" value={ratio} variant="determinate" />
          </div>
          <div className={classes.priceContainer}>
            <div className={classes.price} style={{ flexBasis: `${ratio}%` }}>
              <Typography display="inline" variant="h6">
                {loading ? loadingText : minText}
              </Typography>
            </div>
            {avgText || loading ? (
              <Typography display="inline" variant="h4">
                {loading ? loadingText : avgText}
              </Typography>
            ) : null}
            <div className={cx(classes.price, classes.rightPrice)}>
              <Typography align="right" display="inline" variant="h6">
                {loading ? loadingText : maxText}
              </Typography>
            </div>
          </div>
        </div>
      </Section>
    );
  },
);

SalarySection.defaultProps = {
  title: '$(pageTerms.pageTerm) salary range',
  subtitle:
    '$(brand.title:nbsp) has the <b>data</b> to help you negotiate to the top of this range',
  search: '',
};

SalarySection.propTypes = {
  isAlternatingColor: PropTypes.bool,
  search: PropTypes.string,
  subtitle: PropTypes.string,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  type: PropTypes.string,
};

export default React.memo(withTrackingContext(SalarySection, SALARY_SECTION_ID));
